// Resolves the pharmacy store that scopes prescribe/search — see
// api/pharmacyStores.js for why every such call needs one.
//
// Selection rule:
//   exactly one pharmacy store  → selected automatically, no UI at all
//   more than one               → the caller renders <PharmacyStoreSelect/>
//   none                        → `error`, and callers must not search
//
// The pick is remembered for the tab so reopening the Orders modal doesn't ask
// the doctor to choose the same counter again.

import { useCallback, useEffect, useState } from "react";
import { fetchPharmacyStores } from "../api/pharmacyStores";

const LAST_STORE_KEY = "ipd_pharmacy_store_id";

const readRememberedStoreId = () => {
  try {
    return sessionStorage.getItem(LAST_STORE_KEY) || "";
  } catch {
    return "";
  }
};

const rememberStoreId = (id) => {
  try {
    if (id) sessionStorage.setItem(LAST_STORE_KEY, String(id));
  } catch {
    /* private mode / quota — the picker just won't be pre-filled */
  }
};

export default function usePharmacyStore() {
  const [stores, setStores] = useState([]);
  const [storeId, setStoreIdState] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    let alive = true;
    setLoading(true);
    fetchPharmacyStores()
      .then((list) => {
        if (!alive) return;
        setStores(list);
        if (list.length === 0) {
          setError("No active pharmacy store is configured for this hospital.");
          return;
        }
        setError("");
        // One store = nothing to choose. Otherwise fall back to whatever was
        // picked earlier in this tab, but only if it's still in the list.
        const remembered = readRememberedStoreId();
        const preselect =
          list.length === 1
            ? list[0].id
            : list.some((s) => s.id === remembered)
              ? remembered
              : "";
        setStoreIdState((prev) => prev || preselect);
      })
      .catch((err) => {
        if (alive) setError(err.message || "Could not load pharmacy stores.");
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, []);

  const setStoreId = useCallback((next) => {
    const value = next ? String(next) : "";
    setStoreIdState(value);
    rememberStoreId(value);
  }, []);

  return {
    stores,
    storeId,
    setStoreId,
    loading,
    error,
    // The only case that needs UI: more than one store to choose between.
    showPicker: stores.length > 1,
    // Guard for callers — searching before this is true would be unscoped.
    ready: Boolean(storeId),
  };
}
